<template>
  <!-- 操作栏 -->
  <el-row justify="end">
    <el-button @click="openForm">
      <Icon class="mr-5px" icon="system-uicons:contacts" />
      创建联系人
    </el-button>
    <el-button
      v-if="queryParams.businessId"
      v-hasPermi="['crm:contact:create-business']"
      @click="openBusinessModal"
    >
      <Icon class="mr-5px" icon="ep:circle-plus" />
      关联
    </el-button>
    <el-button
      v-if="queryParams.businessId"
      v-hasPermi="['crm:contact:delete-business']"
      @click="deleteContactBusinessList"
    >
      <Icon class="mr-5px" icon="ep:remove" />
      解除关联
    </el-button>
  </el-row>

  <!-- 列表 -->
  <ContentWrap class="mt-10px">
    <el-table
      ref="contactRef"
      v-loading="loading"
      :data="list"
      :show-overflow-tooltip="true"
      :stripe="true"
    >
      <el-table-column v-if="queryParams.businessId" type="selection" width="55" />
      <!-- ★ 列 / 列宽与「联系人管理」(views/crm/contact/index.vue) 保持一致 (2026-09-20)
           唯一有意差异：不显示「客户名称」列 —— 本组件只用于客户详情 / 商机详情的
           「联系人」页签，列表已按单个客户过滤，该列恒为同一个值，纯占宽度。
           也不带「操作」列：这里是查看+新建入口，编辑/删除请去联系人管理。 -->
      <el-table-column align="center" fixed="left" label="联系人姓名" prop="name" width="160">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="手机" prop="mobile" width="120" />
      <el-table-column align="center" label="电话" prop="telephone" width="130" />
      <el-table-column align="center" label="邮箱" prop="email" width="180" />
      <el-table-column align="center" label="籍贯" prop="nativePlace" width="120" />
      <el-table-column align="center" label="网址" prop="website" width="200" />
      <el-table-column align="center" label="邮编" prop="postCode" width="100" />
      <el-table-column align="center" label="职位" prop="post" width="120" />
      <el-table-column align="center" label="关键决策人" prop="master" width="100">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.INFRA_BOOLEAN_STRING" :value="scope.row.master" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="直属上级" prop="parentName" width="160">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.parentId)">
            {{ scope.row.parentName }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="地址" prop="detailAddress" width="180" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="下次联系时间"
        prop="contactNextTime"
        width="180px"
      />
      <el-table-column align="center" label="性别" prop="sex">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.SYSTEM_USER_SEX" :value="scope.row.sex" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="备注" prop="remark" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="最后跟进时间"
        prop="contactLastTime"
        width="180px"
      />
      <el-table-column align="center" label="负责人" prop="ownerUserName" width="120" />
      <el-table-column align="center" label="所属部门" prop="ownerUserDeptName" width="100" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="更新时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180px"
      />
      <el-table-column align="center" label="创建人" prop="creatorName" width="120" />
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加 -->
  <ContactForm ref="formRef" @success="getList" />
  <!-- 关联商机选择弹框 -->
  <ContactListModal
    v-if="customerId"
    ref="contactModalRef"
    :customer-id="customerId"
    @success="createContactBusinessList"
  />
</template>
<script lang="ts" setup>
import * as ContactApi from '@/api/crm/contact'
import ContactForm from './../ContactForm.vue'
import { DICT_TYPE } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import { BizTypeEnum } from '@/api/crm/permission'
import ContactListModal from './ContactListModal.vue'

defineOptions({ name: 'CrmContactList' })
const props = defineProps<{
  bizType: number // 业务类型
  bizId: number // 业务编号
  customerId?: number // 特殊：客户编号；在【商机】详情中，可以传递客户编号，默认新建的联系人关联到该客户
  businessId?: number // 特殊：商机编号；在【商机】详情中，可以传递商机编号，默认新建的联系人关联到该商机
}>()

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  customerId: undefined as unknown, // 允许 undefined + number
  businessId: undefined as unknown // 允许 undefined + number
})
const message = useMessage()

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    // 置空参数
    queryParams.customerId = undefined
    // 执行查询
    let data = { list: [], total: 0 }
    switch (props.bizType) {
      case BizTypeEnum.CRM_CUSTOMER:
        queryParams.customerId = props.bizId
        data = await ContactApi.getContactPageByCustomer(queryParams)
        break
      case BizTypeEnum.CRM_BUSINESS:
        queryParams.businessId = props.bizId
        data = await ContactApi.getContactPageByBusiness(queryParams)
        break
      default:
        return
    }
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 添加操作 */
const formRef = ref()
const openForm = () => {
  formRef.value.open('create', undefined, props.customerId, props.businessId)
}

/** 打开联系人详情 */
const { push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmContactDetail', params: { id } })
}

/** 打开联系人与商机的关联弹窗 */
const contactModalRef = ref()
const openBusinessModal = () => {
  contactModalRef.value.open()
}
const createContactBusinessList = async (contactIds: number[]) => {
  const data = {
    businessId: props.bizId,
    contactIds: contactIds
  } as ContactApi.ContactBusiness2ReqVO
  contactRef.value.getSelectionRows().forEach((row: ContactApi.ContactVO) => {
    data.contactIds.push(row.id)
  })
  await ContactApi.createContactBusinessList2(data)
  // 刷新列表
  message.success('关联联系人成功')
  handleQuery()
}

/** 解除联系人与商机的关联 */
const contactRef = ref()
const deleteContactBusinessList = async () => {
  const data = {
    businessId: props.bizId,
    contactIds: contactRef.value.getSelectionRows().map((row: ContactApi.ContactVO) => row.id)
  } as ContactApi.ContactBusiness2ReqVO
  if (data.contactIds.length === 0) {
    return message.error('未选择联系人')
  }
  await ContactApi.deleteContactBusinessList2(data)
  // 刷新列表
  message.success('取关联系人成功')
  handleQuery()
}

/** 监听打开的 bizId + bizType，从而加载最新的列表 */
watch(
  () => [props.bizId, props.bizType],
  () => {
    handleQuery()
  },
  { immediate: true, deep: true }
)
</script>
